/**
 * Inverted text search index backed by IndexedDB.
 *
 * Schema:
 *   DB  : "efone-search-index"
 *   Store: "terms"  key = "${userId}:${word}:${messageKey}"
 *
 * Usage:
 *   indexMessages(userId, chatId, messages)  — called on every putMessages
 *   search(userId, chatId, query)            — returns messageKeys ordered by relevance
 *   clearChat(userId, chatId)                — called on chat delete
 *   destroyAll()                             — called on logout
 */

const DB_NAME = "efone-search-index";
const DB_VERSION = 1;
const STORE = "terms";

interface TermEntry {
  key: string;         // `${userId}:${word}:${messageKey}`
  userId: string;
  chatId: string;
  word: string;
  messageKey: string;  // `${userId}:${messageId}` — matches messageCacheDB key format
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "key" });
        store.createIndex("chatId", "chatId", { unique: false });
        store.createIndex("userWord", ["userId", "chatId", "word"], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tokenise(text: string): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length >= 2);
}

const searchIndex = {
  /**
   * Index the text content of messages for a chat.
   * Designed to be called fire-and-forget from putMessages.
   */
  async indexMessages(
    userId: string,
    chatId: string,
    messages: any[],
  ): Promise<void> {
    const entries: TermEntry[] = [];

    for (const msg of messages) {
      if (!msg.id || !msg.message) continue;
      const messageKey = `${userId}:${msg.id}`;
      const words = new Set(tokenise(String(msg.message)));
      words.forEach((word) => {
        entries.push({
          key: `${userId}:${word}:${messageKey}`,
          userId,
          chatId,
          word,
          messageKey,
        });
      });
    }

    if (!entries.length) return;

    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      entries.forEach((e) => store.put(e));
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  },

  /**
   * Full-text search within a chat.
   * Returns messageKeys whose text contains ALL query words (intersection).
   * Single-word queries return all matching keys sorted alphabetically.
   */
  async search(
    userId: string,
    chatId: string,
    query: string,
  ): Promise<string[]> {
    const words = Array.from(new Set(tokenise(query)));
    if (!words.length) return [];

    const db = await openDB();

    const getKeysForWord = (word: string): Promise<string[]> =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const index = tx.objectStore(STORE).index("userWord");
        const range = IDBKeyRange.only([userId, chatId, word]);
        const req = index.getAll(range);
        req.onsuccess = () =>
          resolve((req.result as TermEntry[]).map((e) => e.messageKey));
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => {};
      });

    const sets = await Promise.all(words.map(getKeysForWord));
    db.close();

    if (!sets.length) return [];

    // Intersect: keep only messageKeys that appear in every word's result set
    const [first, ...rest] = sets.map((arr) => new Set(arr));
    const intersection = rest.reduce((acc, set) => {
      acc.forEach((k) => { if (!set.has(k)) acc.delete(k); });
      return acc;
    }, new Set(first));

    return Array.from(intersection);
  },

  /** Remove all index entries for a chat (on chat delete). */
  async clearChat(userId: string, chatId: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const index = tx.objectStore(STORE).index("chatId");
      const req = index.openCursor(IDBKeyRange.only(chatId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const entry = cursor.value as TermEntry;
          if (entry.userId === userId) cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  },

  /** Destroy the entire search index database (called on logout). */
  destroyAll(): Promise<void> {
    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  },
};

export default searchIndex;
